import { useState, useEffect } from 'react';
import { Network, Plus, Search, Edit, Trash2, Save, X, Loader2, ExternalLink, Link } from 'lucide-react';
import { applicationAPI, categoryAPI, userAPI, slaAPI } from '../services/api';
import { toast } from 'sonner';
import Paginator from '../components/ui/Paginator';

const critColors = { critical: 'bg-rose-100 text-rose-800', high: 'bg-amber-100 text-amber-800', medium: 'bg-blue-100 text-blue-800', low: 'bg-slate-100 text-slate-700' };

export function AppOwnersPage() {
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { name: '', identifier: '', criticality: 'medium', description: '', owner: '', technical_owner: '', app_head: '', app_manager: '', app_team_lead: '', approval_levels: 2, auto_assign_enabled: true, escalation_cc_emails: '', dl_email: '', escalation_policy: '', escalation_enabled: true, sla_policy_set: '', auto_close_hours: '', sla_business_hours: '', notify_team_on_create: false, category_ids: [] };
  const [form, setForm] = useState(emptyForm);
  const [escalationPolicies, setEscalationPolicies] = useState([]);
  const [slaPolicySets, setSlaPolicySets] = useState([]);

  // Load applications with server-side pagination + search — guaranteed independent call
  const loadApps = async (pg = page, ps = pageSize, q = search) => {
    try {
      const params = { page: pg, page_size: ps };
      if (q) params.search = q;
      const res = await applicationAPI.list(params);
      const data = res.data?.results || res.data || [];
      setApps(data);
      setTotal(res.data?.count || data.length);
    } catch (err) {
      console.error('[AppOwnersPage] applicationAPI.list() failed:', err?.response?.status, err?.message);
      setApps([]);
    }
  };

  const fetch = async () => {
    setLoading(true);
    await loadApps(page, pageSize, search);
    // Load secondary data — failures here won't affect the app list
    const [cRes, uRes, epRes, spsRes] = await Promise.allSettled([
      categoryAPI.listFlat(),
      userAPI.list({ page_size: 500 }),
      slaAPI.listEscalationPolicies(),
      slaAPI.listPolicySets(),
    ]);
    if (cRes.status === 'fulfilled') setCategories(cRes.value.data?.results || cRes.value.data || []);
    if (uRes.status === 'fulfilled') setUsers(uRes.value.data?.results || uRes.value.data || []);
    if (epRes.status === 'fulfilled') setEscalationPolicies(epRes.value.data?.results || epRes.value.data || []);
    if (spsRes.status === 'fulfilled') setSlaPolicySets(spsRes.value.data?.results || spsRes.value.data || []);
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []); // eslint-disable-line

  // Reload apps when page/pageSize/search changes
  useEffect(() => {
    if (!loading) loadApps(page, pageSize, search);
  }, [page, pageSize]); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
    loadApps(1, pageSize, searchInput);
  };

  const filtered = apps.filter(a => !search || (a.name || '').toLowerCase().includes(search.toLowerCase()));

  const handleSave = async () => {
    if (!form.name || !form.identifier) { toast.error('Name and identifier required'); return; }
    const data = { ...form, owner: form.owner || null, technical_owner: form.technical_owner || null, app_head: form.app_head || null, app_manager: form.app_manager || null, app_team_lead: form.app_team_lead || null, escalation_policy: form.escalation_policy || null, sla_policy_set: form.sla_policy_set || null, auto_close_hours: form.auto_close_hours || null, sla_business_hours: form.sla_business_hours === '' ? null : form.sla_business_hours };
    try {
      if (editing) { await applicationAPI.update(editing, data); toast.success('Updated'); }
      else { await applicationAPI.create(data); toast.success('Created'); }
      setShowForm(false); setEditing(null); setForm(emptyForm); fetch();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await applicationAPI.delete(id); toast.success('Deleted'); fetch(); } catch { toast.error('Failed'); }
  };

  const startEdit = (a) => {
    setForm({ name: a.name, identifier: a.identifier, criticality: a.criticality, description: a.description || '', owner: a.owner || '', technical_owner: a.technical_owner || '', app_head: a.app_head || '', app_manager: a.app_manager || '', app_team_lead: a.app_team_lead || '', approval_levels: a.approval_levels || 2, auto_assign_enabled: a.auto_assign_enabled !== false, escalation_cc_emails: a.escalation_cc_emails || '', dl_email: a.dl_email || '', escalation_policy: a.escalation_policy || '', escalation_enabled: a.escalation_enabled !== false, sla_policy_set: a.sla_policy_set || '', auto_close_hours: a.auto_close_hours || '', sla_business_hours: a.sla_business_hours === null || a.sla_business_hours === undefined ? '' : a.sla_business_hours, notify_team_on_create: a.notify_team_on_create === true, category_ids: a.category_names ? a.category_names.map(c => c.id) : [] });
    setEditing(a.id); setShowForm(true);
  };

  const toggleCat = (catId) => setForm(p => ({ ...p, category_ids: p.category_ids.includes(catId) ? p.category_ids.filter(id => id !== catId) : [...p.category_ids, catId] }));

  return (
    <div data-testid="app-owners-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2"><Network className="w-6 h-6 text-cyan-600" /> Applications / Systems</h2>
          <p className="text-sm text-slate-500 mt-1">Manage applications, systems, owners, approvers, and linked categories</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm); }} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600"><Plus className="w-4 h-4" /> Add Application</button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="Search applications..." className="w-full pl-9 pr-4 py-2.5 border dark:border-slate-700 rounded-xl text-sm dark:bg-slate-800 dark:text-white" />
          </div>
          <button type="submit" className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-4 py-2 rounded-xl text-sm dark:text-white hover:bg-slate-50">Search</button>
          {search && <button type="button" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); loadApps(1, pageSize, ''); }} className="px-3 py-2 rounded-xl text-xs bg-slate-100 dark:bg-slate-700 text-slate-500">Clear</button>}
        </form>
        <span className="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">{total} application{total !== 1 ? 's' : ''} total</span>
      </div>

      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6">
          <h3 className="font-semibold mb-4 dark:text-white">{editing ? 'Edit' : 'New'} Application</h3>

          {/* Basic Info */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <input placeholder="Application Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            <input placeholder="Identifier *" value={form.identifier} onChange={e => setForm({...form, identifier: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            <select value={form.criticality} onChange={e => setForm({...form, criticality: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
              <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>

          {/* Owners */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Business Owner</label>
              <select value={form.owner} onChange={e => setForm({...form, owner: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
                <option value="">Select...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Technical Owner</label>
              <select value={form.technical_owner} onChange={e => setForm({...form, technical_owner: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
                <option value="">Select...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
              </select>
            </div>
          </div>

          {/* Approval Chain */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 mb-4 border dark:border-slate-700">
            <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-300 mb-3">Approval Chain (for SR/CR)</h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Approval Levels</label>
                <select value={form.approval_levels} onChange={e => setForm({...form, approval_levels: parseInt(e.target.value)})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white">
                  <option value={0}>No Approval</option>
                  <option value={2}>2 Levels (Lead → Manager)</option>
                  <option value={3}>3 Levels (Lead → Manager → Head)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">L1: Team Lead</label>
                <select value={form.app_team_lead} onChange={e => setForm({...form, app_team_lead: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white">
                  <option value="">Select...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">L2: App Manager</label>
                <select value={form.app_manager} onChange={e => setForm({...form, app_manager: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white">
                  <option value="">Select...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
              </div>
              {form.approval_levels >= 3 && (
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">L3: App Head</label>
                  <select value={form.app_head} onChange={e => setForm({...form, app_head: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white">
                    <option value="">Select...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                  </select>
                </div>
              )}
            </div>
            {/* Auto-Assign toggle — consistent card layout */}
            <div className="mt-4 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Auto-Assign</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {form.auto_assign_enabled ? 'SR/CR will be auto-assigned to IT support after approval' : 'Last approver will manually assign SR/CR to IT support'}
                </p>
              </div>
              <button type="button" onClick={() => setForm({...form, auto_assign_enabled: !form.auto_assign_enabled})}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ml-4 flex-shrink-0 transition-colors ${form.auto_assign_enabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'}`}>
                <div className={`w-8 h-4 rounded-full relative transition-colors ${form.auto_assign_enabled ? 'bg-emerald-500' : 'bg-slate-400'}`}>
                  <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${form.auto_assign_enabled ? 'left-4' : 'left-0.5'}`} />
                </div>
                {form.auto_assign_enabled ? 'ON' : 'OFF'}
              </button>
            </div>
            {/* CC Emails for Escalation */}
            <div className="mt-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Escalation CC Emails (comma-separated)</label>
              <input type="text" placeholder="cto@trivont.com, it-head@trivont.com" value={form.escalation_cc_emails} onChange={e => setForm({...form, escalation_cc_emails: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white" />
              <p className="text-xs text-slate-400 mt-1">These emails will be CC'd on all escalation and SLA breach notifications for this application</p>
            </div>
            {/* DL Email — all ticket notifications */}
            <div className="mt-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Distribution List (DL) Email</label>
              <input type="email" placeholder="dl-itsupport@company.com" value={form.dl_email || ''} onChange={e => setForm({...form, dl_email: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white" />
              <p className="text-xs text-slate-400 mt-1">All ticket emails for this application (created, assigned, status, approvals, SLA breach) are also sent to this DL</p>
            </div>
            {/* Escalation Policy + toggle — consistent card layout */}
            <div className="mt-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Escalation Policy</label>
              <select value={form.escalation_policy} onChange={e => setForm({...form, escalation_policy: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white mb-2">
                <option value="">None (no escalation)</option>
                {escalationPolicies.map(p => <option key={p.id} value={p.id}>{p.name} ({p.l2_threshold_percent}%/{p.l3_threshold_percent}%/{p.l4_threshold_percent}%)</option>)}
              </select>
              <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Escalation</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {form.escalation_enabled ? 'Escalation is active for this application' : 'Escalation is disabled for this application'}
                  </p>
                </div>
                <button type="button" onClick={() => setForm({...form, escalation_enabled: !form.escalation_enabled})}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ml-4 flex-shrink-0 transition-colors ${form.escalation_enabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'}`}>
                  <div className={`w-8 h-4 rounded-full relative transition-colors ${form.escalation_enabled ? 'bg-emerald-500' : 'bg-slate-400'}`}>
                    <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${form.escalation_enabled ? 'left-4' : 'left-0.5'}`} />
                  </div>
                  {form.escalation_enabled ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
            {/* SLA Policy Set */}
            <div className="mt-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">SLA Policy Set (Application-Specific)</label>
              <select value={form.sla_policy_set} onChange={e => setForm({...form, sla_policy_set: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white">
                <option value="">Global Default (use tenant-wide SLA policies)</option>
                {slaPolicySets.map(ps => <option key={ps.id} value={ps.id}>{ps.name}</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">Override SLA timings for tickets in this application. Create policy sets on the SLA Policies page.</p>
            </div>
            {/* Auto-Close Hours */}
            <div className="mt-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Auto-Close After Resolution</label>
              <select value={form.auto_close_hours} onChange={e => setForm({...form, auto_close_hours: e.target.value ? parseInt(e.target.value) : ''})}
                className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white">
                <option value="">Global Default (72h)</option>
                <option value="12">12 hours — Fast-track apps (critical systems)</option>
                <option value="24">24 hours — Standard business apps</option>
                <option value="48">48 hours — Non-urgent internal tools</option>
                <option value="72">72 hours — Default (3 days)</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">Resolved tickets for this application will auto-close after this period if not reopened.</p>
            </div>
            {/* SLA Business Hours Override */}
            <div className="mt-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">SLA Calculation Mode (Override)</label>
              <select value={form.sla_business_hours} onChange={e => setForm({...form, sla_business_hours: e.target.value === '' ? '' : e.target.value === 'true'})}
                className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-800 dark:text-white">
                <option value="">Use Global Setting (default)</option>
                <option value="false">Force 24/7 Calendar — always calendar time</option>
                <option value="true">Force Business Hours — always use working hours</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">Overrides the global SLA mode for tickets in this application only.</p>
            </div>
            {/* Team Notification on New Ticket */}
            <div className="mt-4 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Notify Team on New Ticket</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    When enabled, all team members linked to this application receive an email whenever a new ticket is raised.
                  </p>
                </div>
                <button type="button" onClick={() => setForm({...form, notify_team_on_create: !form.notify_team_on_create})}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ml-4 flex-shrink-0 transition-colors ${form.notify_team_on_create ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'}`}>
                  <div className={`w-8 h-4 rounded-full relative transition-colors ${form.notify_team_on_create ? 'bg-sky-500' : 'bg-slate-400'}`}>
                    <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${form.notify_team_on_create ? 'left-4' : 'left-0.5'}`} />
                  </div>
                  {form.notify_team_on_create ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="mb-4">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5 mb-2"><Link className="w-4 h-4 text-sky-500" /> Linked Categories</label>
            <div className="flex flex-wrap gap-2">
              {categories.map(c => (
                <button key={c.id} onClick={() => toggleCat(c.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.category_ids.includes(c.id) ? 'bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900 dark:text-sky-300' : 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-700 dark:text-slate-400 hover:border-sky-300'}`}>
                  {form.category_ids.includes(c.id) ? '✓ ' : ''}{c.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={handleSave} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 hover:bg-sky-600"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-xl text-sm"><X className="w-4 h-4 inline mr-1" />Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Application</th>
                <th className="px-4 py-3 text-center font-semibold">Criticality</th>
                <th className="px-4 py-3 text-left font-semibold">Owner</th>
                <th className="px-4 py-3 text-center font-semibold">Approval</th>
                <th className="px-4 py-3 text-left font-semibold">Approval Chain</th>
                <th className="px-4 py-3 text-left font-semibold">SLA Policy Set</th>
                <th className="px-4 py-3 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700 text-sm">
              {apps.map(app => (
                <tr key={app.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                  <td className="px-4 py-3">
                    <div className="font-medium dark:text-white">{app.name}</div>
                    <div className="text-xs text-slate-400 flex items-center gap-1"><ExternalLink className="w-3 h-3" />{app.identifier}</div>
                  </td>
                  <td className="px-4 py-3 text-center"><span className={`${critColors[app.criticality] || 'bg-slate-100'} px-2.5 py-1 rounded-full text-xs font-semibold`}>{app.criticality}</span></td>
                  <td className="px-4 py-3 dark:text-slate-300">{app.owner_name || '-'}</td>
                  <td className="px-4 py-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-medium ${app.approval_levels === 0 ? 'bg-slate-100 text-slate-600' : app.approval_levels === 2 ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{app.approval_levels === 0 ? 'None' : `${app.approval_levels} Levels`}</span>
                    {app.approval_levels > 0 && <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${app.auto_assign_enabled !== false ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{app.auto_assign_enabled !== false ? 'Auto' : 'Manual'}</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                    {app.app_team_lead_name && <span>L1: {app.app_team_lead_name}</span>}
                    {app.app_manager_name && <span className="ml-2">L2: {app.app_manager_name}</span>}
                    {app.app_head_name && <span className="ml-2">L3: {app.app_head_name}</span>}
                    {!app.app_team_lead_name && !app.app_manager_name && '-'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {app.sla_policy_set_name
                      ? <span className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-1 rounded-full font-medium">{app.sla_policy_set_name}</span>
                      : <span className="text-slate-400">Global Default</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => startEdit(app)} className="text-slate-400 hover:text-sky-600 p-1"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(app.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {apps.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">{loading ? 'Loading...' : search ? `No applications matching "${search}"` : 'No applications'}</td></tr>}
            </tbody>
          </table>
          <Paginator total={total} page={page} pageSize={pageSize} onPage={p => { setPage(p); loadApps(p, pageSize, search); }} onPageSize={s => { setPageSize(s); setPage(1); loadApps(1, s, search); }} />
        </div>
      )}
    </div>
  );
}
